
import React, { PropsWithChildren, ReactElement, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './CustomerService.css';
import FaqMenuContactFooter from '../FaqMenu/components/ContactFooter/FaqMenuContactFooter';
import { Button, Container, Title } from '../../importCL';
import { useWidthContext } from '../../providers/Width';
import GoogleSearchInput from '../GoogleSearchInput/GoogleSearchInput';
import { InputDropdown } from 'component-library/index';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

interface ICustomerServiceTab {
    name?: string;
    heading?: string;
    cta?: {
        ctaVariant?: 'solid' | 'ghosted-light' | 'ghosted-dark' | 'link-light' | 'link';
        ctaSize?: 'small' | 'medium' | 'large';
        ctaText?: string;
        ctaLink?: string;
        ctaAlignment?: 'left' | 'center' | 'right';
    };
    topQuestions?: Array<{question: string, answer: string}>;
}

interface IIcon {
    iconLabel: string;
    iconPath: string;
    iconAltText: string;
    uuid: string;
}

interface IContactInformation {
    icon?: IIcon;
    firstTitle?: string;
    firstDescription?: string;
    secondTitle?: string;
    secondDescription?: string;
}

export interface ICustomerServiceProps extends MappedComponentProperties {
    tabs?: Array<ICustomerServiceTab>;
    contactBackgroundImage?: string;
    contactTitle?: string;
    contactInformation?: Array<IContactInformation>;
}

export const CustomerServiceEditConfig = {
    emptyLabel: 'Customer Service',
    isEmpty: (props: PropsWithChildren<ICustomerServiceProps>) => {
        return !props || !props.tabs;
    }
};

const CustomerService = (props: PropsWithChildren<ICustomerServiceProps>): JSX.Element => {
    const { width } = useWidthContext();
    const [searchInput, setSearchInput] = useState<string>('');
    const [currentCategory, setCurrentCategory] = useState<string>(props.tabs && props.tabs.length ? props.tabs[0].name : '');
    const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);

    const redirectToResults = () => {
        const resultsUrl = isAuthor ? '/content/cob-corp-acquisition/us/en/faqs/search.html' : '/faqs/search';

        if (!searchInput) {
            return;
        }
        const url = `${resultsUrl}?q=${searchInput}`;
        window.location.assign(url);
    };

    const getMenu = (): JSX.Element => {
        if (!props.tabs) {
            return <React.Fragment />;
        }

        return width > 768 ? (
            <div className='CustomerService__list'>
                {props.tabs.map((tab, index) => (
                    <a
                        key={index}
                        className='CustomerService__categoryButton'
                        onClick={(e) => { e.preventDefault(); setCurrentCategory(tab.name)}}
                    >
                        <div className={`CustomerService__category--${tab.name === currentCategory ? 'selected' : 'unselected'}`}>
                            <div className={`CustomerService__categoryIndicator--${tab.name === currentCategory ? 'selected' : 'unselected'}`}>&ensp;</div>
                            {tab.name}
                        </div>
                    </a>
                ))}
            </div>
        ) : (
            <div className='CustomerService__list'>
                <InputDropdown
                    id='sidenav-dropdown'
                    label='Products'
                    name='customer-service-products'
                    options={props.tabs.map(tab => {
                        return {
                            option: tab.name, 
                            value: tab.name,
                        }
                    })}
                    value={currentCategory}
                    setValue={setCurrentCategory}
                />
            </div>
        )
    }

    const getQuestions = (): JSX.Element => {
        if (!props.tabs || !props.tabs.length) {
            return <React.Fragment />
        }

        const currentTab = props.tabs.find(tab => tab.name === currentCategory);

        if (!currentTab || !currentTab.topQuestions || !currentTab.topQuestions.length) {
            return <React.Fragment />
        }

        const ctaAlignmentMap = {
            left: 'start',
            center: 'center',
            right: 'end',
        };

        return (
            <React.Fragment>
                <Title tag='h2' verticalBar className='CustomerService_questionsCategory md-text-2r md:mt-8 mt-10'>
                    {currentTab.heading}
                </Title>
                {currentTab.topQuestions.map((question, index) => (
                    <div className='CustomerService__question no-underline'>
                        <LinkWrapper 
                            key={index}
                            href={question.answer}
                        >
                            {transformHTMLParserLinks(question.question)}
                        </LinkWrapper>
                    </div>
                ))}
                {currentTab.cta && currentTab.cta.ctaText && currentTab.cta.ctaLink ? (
                    <div className={`CustomerService__faqLinksButtonContainer self-${ctaAlignmentMap[currentTab.cta.ctaAlignment]}`}>
                        <Button 
                            className='CustomerService__faqLinksButton' 
                            icon='chevron'
                            variant={currentTab.cta.ctaVariant || 'solid'}
                            size={currentTab.cta.ctaSize} 
                            href={currentTab.cta.ctaLink}>
                            {transformHTMLParserLinks(currentTab.cta.ctaText) as unknown as ReactElement}
                        </Button>
                    </div>
                ) : (
                    <React.Fragment />
                )}
            </React.Fragment>
        )
    }

    return (
        <div className='CustomerService'>
            <Container id='customer-service-menu' className='flex flex-row flex-nowrap md:flex-col md:p-0 md:gap-0'>
                {getMenu()}
                <section className='CustomerService__FaqLinksContainer flex flex-col md:pt-0 md:px-4 py-0 px-4'>
                    {width > 768 ? (
                            <div className='CustomerService__googleSearchInput flex flex-row'>
                                <GoogleSearchInput
                                    placeHolderText='Search Frequently Asked Questions'
                                    aria-label='search'
                                    showX={!!searchInput}
                                    inputValue={searchInput}
                                    setInputValue={setSearchInput}
                                    resultsUrl='/faqs/search'
                                    onXClick={() => {
                                        setSearchInput(''); 
                                    }}
                                />
                                <Button variant='solid' size='medium' onClick={redirectToResults}>Search</Button>
                            </div>
                        ) : (
                            <React.Fragment></React.Fragment>
                        )
                    }
                    {getQuestions()}
                </section>
            </Container>
            {props?.contactTitle && 
                <React.Fragment>
                    <FaqMenuContactFooter
                        backgroundImage={props?.contactBackgroundImage}
                        title={props?.contactTitle}
                        contactInformation={props?.contactInformation}
                    />
                </React.Fragment>
            }
        </div>
    )
};

export default CustomerService;
        